'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ArrowRight, Play, ChevronDown } from 'lucide-react';

const slides = [
{
  id: 'slide-kedarkantha',
  image: "https://images.unsplash.com/photo-1618085609546-7eac5b37d2b0",
  alt: 'Snow-covered Himalayan mountain peaks at sunrise with orange sky',
  tagline: 'Conquer the Himalayas',
  headline: 'Where Every Trail\nTells a Story',
  sub: 'Kedarkantha Winter Trek — 6 days, ₹8,500'
},
{
  id: 'slide-rafting',
  image: "https://images.unsplash.com/photo-1629248564797-8c5ba85da9d3",
  alt: 'Group of adventurers white water rafting on rapid river',
  tagline: 'Feel the Rush',
  headline: 'Ride the Rapids\nof the Ganges',
  sub: 'Rishikesh River Rafting — 2 days, ₹3,200'
},
{
  id: 'slide-spiti',
  image: "https://images.unsplash.com/photo-1585770695688-81cf9fa2b059",
  alt: 'Vast Himalayan landscape with barren mountains and blue sky',
  tagline: 'The Last Frontier',
  headline: 'Ride Through\nSpiti Valley',
  sub: 'Motorcycle Expedition — 9 days, ₹28,000'
}];


export default function HeroBanner() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides?.[current];

  return (
    <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden">
      {/* Background Images */}
      {slides?.map((s, i) =>
      <div
        key={s?.id}
        className={`absolute inset-0 transition-opacity duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
        
          <AppImage
          src={s?.image}
          alt={s?.alt}
          fill
          priority={i === 0}
          className="object-cover"
          sizes="100vw" />
        
        </div>
      )}
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D2540]/85 via-[#0D2540]/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D2540]/60 via-transparent to-transparent" />
      {/* Content */}
      <div className="relative h-full flex items-center">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#E8440A]/20 border border-[#E8440A]/40 text-[#FF6B35] text-sm font-semibold px-4 py-1.5 rounded-full mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-[#E8440A] rounded-full animate-pulse-subtle"></span>
              {slide?.tagline}
            </div>

            <h1 className="text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-4 whitespace-pre-line animate-fade-in">
              {slide?.headline}
            </h1>

            <p className="text-white/75 text-lg mb-8 font-medium animate-fade-in">
              {slide?.sub}
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in">
              <Link href="/trips-listing-page" className="btn-primary text-base px-8 py-4 shadow-lg">
                Explore All Trips <ArrowRight size={18} />
              </Link>
              <button className="flex items-center gap-2.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-150 active:scale-95">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <Play size={14} className="text-[#1A3C5E] ml-0.5" />
                </div>
                Watch Stories
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Slide Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
        {slides?.map((_, i) =>
        <button
          key={`indicator-${i}`}
          onClick={() => setCurrent(i)}
          className={`transition-all duration-300 rounded-full ${
          i === current ? 'w-8 h-2 bg-[#E8440A]' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`
          }
          aria-label={`Go to slide ${i + 1}`} />

        )}
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-10 hidden lg:flex flex-col items-center gap-2 text-white/60">
        <span className="text-xs font-medium tracking-widest uppercase rotate-90 mb-2">Scroll</span>
        <ChevronDown size={16} className="animate-bounce" />
      </div>
    </section>);

}